/**
 * Tactics puzzles — curated positions with a single correct idea, verified
 * programmatically (scripts/verify-puzzles.ts) so a puzzle can never ship with
 * an illegal or impossible solution. Red is always to move in these.
 */

import type { Bi } from "./lessons.js";

export type PuzzleTask =
  | { kind: "mate" } // any move that delivers checkmate
  | { kind: "move"; solution: string[] }; // one of these exact UCI moves

export interface Puzzle {
  id: string;
  fen: string;
  theme: Bi;
  difficulty: "easy" | "medium" | "hard";
  task: PuzzleTask;
  hint?: Bi;
}

const MATE: Bi = { zh: "一步杀", en: "Mate in 1", vi: "Chiếu bí 1 nước" };
const FORK: Bi = { zh: "捉双", en: "Double attack", vi: "Bắt đôi" };
const WIN: Bi = { zh: "得子", en: "Win material", vi: "Ăn quân" };

/**
 * Puzzles edited or added in the admin console, folded into the set below.
 *
 * Nothing arrives here unverified: the server checks every save against
 * @xq/rules before storing it, so a puzzle with an impossible answer cannot be
 * written, let alone served. A failed fetch leaves the shipped set in place.
 */
export async function applyPuzzleOverrides(): Promise<void> {
  try {
    const res = await fetch("/api/puzzles/overrides");
    if (!res.ok) return;
    const body = (await res.json()) as {
      overrides?: Record<string, Record<string, unknown>>;
      added?: Puzzle[];
    };
    for (const [id, patch] of Object.entries(body.overrides ?? {})) {
      const puzzle = PUZZLES.find((p) => p.id === id);
      if (puzzle && patch) Object.assign(puzzle, patch);
    }
    for (const extra of body.added ?? []) {
      // Defensive: an id that already exists would shadow a shipped puzzle.
      if (!PUZZLES.some((p) => p.id === extra.id)) PUZZLES.push(extra);
    }
  } catch {
    /* the shipped set is a perfectly good fallback */
  }
}

import { GENERATED_PUZZLES } from "./puzzles-generated.js";

export const PUZZLES: Puzzle[] = [
  // The hand-written few come first: they are the gentlest introductions and
  // the ones a beginner should meet before the generated corpus.
  {
    id: "p-mate-2rook-a",
    fen: "4k4/9/9/9/9/9/9/R8/3R1R3/3K5 w - - 0 1",
    theme: MATE, difficulty: "easy", task: { kind: "mate" },
    hint: { zh: "沿中路将军。", en: "Check down the centre file.", vi: "Chiếu dọc lộ giữa." },
  },
  {
    id: "p-mate-2rook-b",
    fen: "3k5/9/9/9/9/9/9/9/R3R4/4K4 w - - 0 1",
    theme: MATE, difficulty: "easy", task: { kind: "mate" },
    hint: { zh: "把另一只车开到将的直线上。", en: "Bring the other chariot onto the king's file.", vi: "Đưa Xe còn lại vào lộ của Tướng." },
  },
  {
    id: "p-fork-horse",
    fen: "4k4/9/9/2r3r2/9/9/3N5/9/9/4K4 w - - 0 1",
    theme: FORK, difficulty: "easy", task: { kind: "move", solution: ["d3e5"] },
    hint: { zh: "一步马，同时捉两只车。", en: "One horse move attacks both chariots.", vi: "Một nước Mã tấn công cả hai Xe." },
  },
  {
    id: "p-fork-rook",
    fen: "4k4/9/9/9/1n5c1/9/9/9/4R4/4K4 w - - 0 1",
    theme: FORK, difficulty: "easy", task: { kind: "move", solution: ["e1e5"] },
    hint: { zh: "把车开到同时攻击马和炮的横线。", en: "Occupy the rank that hits both the horse and cannon.", vi: "Chiếm hàng ngang tấn công cả Mã lẫn Pháo." },
  },
  {
    id: "p-win-rook-cannon",
    fen: "4k4/9/9/9/4r4/9/9/9/4C4/4K4 w - - 0 1",
    theme: WIN, difficulty: "easy", task: { kind: "move", solution: ["e1e4"] },
    hint: { zh: "炮打无根的车。", en: "The cannon takes the undefended chariot.", vi: "Pháo ăn Xe không được bảo vệ." },
  },
  ...GENERATED_PUZZLES,
];

// ---- progress ----
const KEY = "xq-puzzles-solved";
export function solvedSet(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(KEY) ?? "[]") as string[]); } catch { return new Set(); }
}
export function markSolved(id: string) {
  const s = solvedSet();
  s.add(id);
  localStorage.setItem(KEY, JSON.stringify([...s]));
}
